import { CommonModule, DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2
} from '@angular/core';
import { of, Subscription, switchMap } from 'rxjs';
import { DeleteConfirmDialogComponent } from '../delete-confirm-dialog/delete-confirm-dialog.component';
import { CancelActivityDialogComponent } from '../cancel-activity-dialog/cancel-activity-dialog.component';
import { CancelPaidActivityDialogComponent } from '../cancel-paid-activity-dialog/cancel-paid-activity-dialog.component';
import { ActivitiesManagementApiService } from '../../../services/activities-management/activities-management-api.service';
import { ActivitiesManagementPhotoService } from '../../../services/activities-management/activities-management-photo.service';
import { ActivityPreviewStateService } from '../../../services/activities-management/activity-preview-state.service';
import type {
  EditGroupActivity,
  ManagementDeleteTarget,
  SportOption,
  SportTypeConfig
} from '../../models/activities-management.model';
import {
  ActivityDrawerSessionState,
  createEmptyActivityDrawerSessionState
} from '../../models/activity-drawer-session.model';
import { ManagementToastTimer } from '../../utils/management-toast-timer.util';
import {
  getSportIconPath,
  getSportLabelFromValue,
  getSportOptionValue
} from '../../utils/management-sport.util';
import { ActivityPreviewDrawerComponent } from '../activity-preview-drawer/activity-preview-drawer.component';

export type ActivityDrawerDeleteRequest = {
  activity: EditGroupActivity;
  deleteTarget: ManagementDeleteTarget;
};

@Component({
  selector: 'bo-activity-drawer-host',
  standalone: true,
  imports: [CommonModule, ActivityPreviewDrawerComponent, DeleteConfirmDialogComponent, CancelActivityDialogComponent, CancelPaidActivityDialogComponent],
  templateUrl: './activity-drawer-host.component.html',
  styleUrl: './activity-drawer-host.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ActivityDrawerHostComponent implements OnInit, OnDestroy {
  @Input() embeddedInGroups = false;
  @Input() embeddedInUserDetail = false;
  @Input() sportTypeConfigs: SportTypeConfig[] = [];
  @Input() sportOptions: SportOption[] = [];
  @Input() defaultCoverPhotoUrl = 'assets/images/group-cover-default.svg';
  @Input() coveredByDialog = false;
  @Input() externalOperationError = '';
  @Input() manageDeleteDialog = false;
  @Input() dismissibleOnMaskClick = false;

  @Output() sessionChange = new EventEmitter<void>();
  @Output() visibilityChange = new EventEmitter<boolean>();
  @Output() externalOperationErrorChange = new EventEmitter<string>();
  @Output() deleteRequested = new EventEmitter<ActivityDrawerDeleteRequest>();
  @Output() activityDeleted = new EventEmitter<EditGroupActivity>();
  @Output() activityCancelled = new EventEmitter<EditGroupActivity>();
  @Output() activitySaved = new EventEmitter<EditGroupActivity>();
  /** View-mode footer Close: same as subgroup drawer — parent should close activity + subgroup + group. */
  @Output() nestedDrawersCloseRequested = new EventEmitter<void>();

  readonly state: ActivityDrawerSessionState = createEmptyActivityDrawerSessionState();

  deleteDialogVisible = false;
  deleteDialogClosing = false;
  deleteConfirmed = false;
  drawerRendered = false;
  participantsLoading = false;

  cancelDialogVisible = false;
  cancelDialogClosing = false;
  skipCancelDialogEnterAnimation = false;
  cancelConfirmed = false;

  cancelPaidDialogVisible = false;
  cancelPaidDialogClosing = false;
  skipCancelPaidDialogEnterAnimation = false;
  cancelPaidConfirmed = false;
  private readonly subscriptions = new Subscription();
  private readonly toastDismiss = new ManagementToastTimer();
  private initialized = false;
  private keepUndoInEditMode = false;
  private hydratingActivityId = '';
  private drawerBackdropElement: HTMLElement | null = null;
  private drawerBackdropClickUnlisten: (() => void) | null = null;
  private readonly onOutsideDrawerPointerDown = (event: Event) => {
    if (!this.shouldUseOutsideDismiss()) {
      return;
    }

    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    if (this.isEventInsideActivityDrawer(target)) {
      return;
    }

    this.dismissOutsideDrawer(event);
  };
  private readonly onOutsideDrawerClick = (event: Event) => {
    if (!this.shouldUseOutsideDismiss()) {
      return;
    }

    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    if (this.isEventInsideActivityDrawer(target)) {
      return;
    }

    this.blockOutsideDrawerClick(event);
  };

  constructor(
    private activitiesRealApi: ActivitiesManagementApiService,
    readonly activityPreview: ActivityPreviewStateService,
    private photoService: ActivitiesManagementPhotoService,
    private cdr: ChangeDetectorRef,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) { }

  ngOnInit() {
    if (!this.initialized && !this.embeddedInGroups) {
      this.ensureActivitiesStoreReady();
    }

    this.document.addEventListener('pointerdown', this.onOutsideDrawerPointerDown, true);
    this.document.addEventListener('click', this.onOutsideDrawerClick, true);
  }

  ngOnDestroy() {
    this.document.removeEventListener('pointerdown', this.onOutsideDrawerPointerDown, true);
    this.document.removeEventListener('click', this.onOutsideDrawerClick, true);
    this.removeDrawerBackdropFromBody();
    this.subscriptions.unsubscribe();
    this.clearToastTimeout();
  }

  get visible(): boolean {
    return this.state.visible;
  }

  get activityDraft(): EditGroupActivity | null {
    return this.state.draft;
  }

  get skipEnterAnimation(): boolean {
    return this.state.skipEnterAnimation;
  }

  get scrollPosition(): number {
    return this.state.scrollPosition;
  }

  get backButtonIcon(): string {
    return this.isNestedDrawerContext ? 'pi-arrow-right' : 'pi-times';
  }

  get backButtonLabel(): string {
    if (this.embeddedInUserDetail) {
      return 'Back to user details';
    }

    if (this.embeddedInGroups) {
      return 'Back to previous drawer';
    }

    return 'Close activity detail';
  }

  private get isNestedDrawerContext(): boolean {
    return this.embeddedInGroups || this.embeddedInUserDetail;
  }

  isEditActionMode(): boolean {
    return (
      this.state.editingName ||
      this.state.editingDescription ||
      this.state.editingRouteUrl ||
      this.state.editingSport ||
      this.state.editingLevel ||
      this.state.coverPhotoMenuVisible ||
      this.activityPreview.hasChanges(
        this.state.draft,
        this.state.original,
        this.state.level,
        this.state.originalLevel,
        this.defaultCoverPhotoUrl
      )
    );
  }

  getSportIconPath = (sport: string) => getSportIconPath(sport, this.sportTypeConfigs);

  open(activity: EditGroupActivity, options?: { editMode?: boolean; keepUndoInEditMode?: boolean; skipEnterAnimation?: boolean }) {
    this.keepUndoInEditMode = !!options?.keepUndoInEditMode;
    this.participantsLoading = false;
    this.state.scrollPosition = 0;
    const draft = this.resolveActivityDetail(activity);
    this.state.draft = { ...draft };
    this.state.original = { ...draft };
    this.state.level = this.activityPreview.getLevelValue(activity.level, activity.sport, this.sportTypeConfigs);
    this.state.originalLevel = this.state.level;
    this.state.editingName = !!options?.editMode;
    this.state.editingDescription = !!options?.editMode;
    this.state.editingRouteUrl = !!options?.editMode;
    this.state.editingSport = !!options?.editMode;
    this.state.editingLevel = !!options?.editMode;
    this.state.coverPhotoMenuVisible = false;
    this.state.coverPhotoFailed = false;
    this.state.saveSuccessVisible = false;
    this.state.discardSuccessVisible = false;
    this.state.participantsExpanded = false;
    this.state.keepUndoInEditMode = this.keepUndoInEditMode;
    this.state.skipEnterAnimation = !!options?.skipEnterAnimation;
    this.state.visible = true;
    this.drawerRendered = true;
    if (!activity.detailLoaded) {
      this.hydrateActivityDetail(activity.id);
    }
    this.emitVisibilityChange();
    this.emitSessionChange();
    this.syncDrawerBackdropToBody();
    this.cdr.markForCheck();
  }

  openDeleteMode(activity: EditGroupActivity) {
    this.keepUndoInEditMode = false;
    const draft = this.resolveActivityDetail(activity);
    Object.assign(this.state, {
      ...createEmptyActivityDrawerSessionState(),
      draft: { ...draft },
      original: { ...draft },
      level: this.activityPreview.getLevelValue(draft.level, draft.sport, this.sportTypeConfigs),
      originalLevel: this.activityPreview.getLevelValue(draft.level, draft.sport, this.sportTypeConfigs),
      visible: false
    });
    this.deleteConfirmed = false;
    this.deleteDialogClosing = false;
    this.deleteDialogVisible = true;
    this.drawerRendered = false;
    this.emitVisibilityChange();
    this.emitSessionChange();
    this.cdr.detectChanges();
  }

  close() {
    this.keepUndoInEditMode = false;
    this.state.skipEnterAnimation = false;
    this.state.visible = false;
    this.closeDeleteDialog();
    this.emitVisibilityChange();
    this.emitSessionChange();
    this.syncDrawerBackdropToBody();
    this.cdr.markForCheck();
  }

  onBackdropClick() {
    this.close();
  }

  private shouldUseOutsideDismiss(): boolean {
    return this.dismissibleOnMaskClick && this.state.visible;
  }

  private isEventInsideActivityDrawer(target: Element): boolean {
    return !!target.closest('.activity-detail-sidebar.p-sidebar-active') ||
      !!target.closest('.delete-dialog-mask');
  }

  private dismissOutsideDrawer(event: Event) {
    this.close();
    event.preventDefault();
    event.stopPropagation();
  }

  private blockOutsideDrawerClick(event: Event) {
    event.preventDefault();
    event.stopPropagation();
  }

  private shouldUseDimmingBackdrop(): boolean {
    return this.shouldUseOutsideDismiss() && !this.isNestedDrawerContext;
  }

  private syncDrawerBackdropToBody() {
    if (!this.shouldUseDimmingBackdrop()) {
      this.removeDrawerBackdropFromBody();
      return;
    }

    if (!this.drawerBackdropElement) {
      this.drawerBackdropElement = this.renderer.createElement('div');
      this.renderer.addClass(this.drawerBackdropElement, 'management-drawer-backdrop');
      this.drawerBackdropClickUnlisten = this.renderer.listen(
        this.drawerBackdropElement,
        'click',
        () => this.onBackdropClick()
      );
      this.renderer.appendChild(this.document.body, this.drawerBackdropElement);
    }

    this.renderer.setStyle(this.drawerBackdropElement, 'z-index', '1199');
  }

  private removeDrawerBackdropFromBody() {
    this.drawerBackdropClickUnlisten?.();
    this.drawerBackdropClickUnlisten = null;

    if (this.drawerBackdropElement) {
      this.renderer.removeChild(this.document.body, this.drawerBackdropElement);
      this.drawerBackdropElement = null;
    }
  }

  onBackClick() {
    this.close();
  }

  /** Footer "Close" in view mode: subgroup uses closeNestedDrawers(); mirror that when embedded in groups. */
  onFooterClose() {
    if (this.embeddedInGroups) {
      this.nestedDrawersCloseRequested.emit();
      return;
    }
    this.close();
  }

  onHidden() {
    if (this.state.visible) {
      this.close();
      return;
    }

    this.clear();
  }

  onShown() {
    this.emitSessionChange();
  }

  onBodyScroll(event: Event) {
    const body = event.target as HTMLElement | null;
    if (!body) {
      return;
    }

    this.state.scrollPosition = body.scrollTop;
    this.emitSessionChange();
  }

  clear() {
    Object.assign(this.state, createEmptyActivityDrawerSessionState());
    this.participantsLoading = false;
    this.drawerRendered = false;
    this.syncDrawerBackdropToBody();
    this.emitVisibilityChange();
    this.emitSessionChange();
    this.cdr.markForCheck();
  }

  getSessionState(): ActivityDrawerSessionState {
    return {
      ...this.state,
      keepUndoInEditMode: this.keepUndoInEditMode,
      draft: this.state.draft ? { ...this.state.draft } : null,
      original: this.state.original ? { ...this.state.original } : null,
      saveSuccessVisible: false,
      discardSuccessVisible: false
    };
  }

  applySessionState(session: Partial<ActivityDrawerSessionState>) {
    this.keepUndoInEditMode = !!session.keepUndoInEditMode;
    Object.assign(this.state, {
      ...createEmptyActivityDrawerSessionState(),
      ...session,
      keepUndoInEditMode: this.keepUndoInEditMode,
      draft: session.draft ? { ...session.draft } : null,
      original: session.original ? { ...session.original } : null,
      saveSuccessVisible: false,
      discardSuccessVisible: false
    });
    this.drawerRendered = !!this.state.visible;
    if (this.state.visible && this.state.draft?.id && !this.state.draft.detailLoaded) {
      this.hydrateActivityDetail(this.state.draft.id);
    }
    this.emitVisibilityChange();
    this.emitSessionChange();
    this.cdr.markForCheck();
  }

  confirmDeleteFromParent() {
    if (!this.state.draft?.id) {
      return;
    }

    if (this.manageDeleteDialog && !this.deleteConfirmed) {
      return;
    }

    const deletedActivity = { ...this.state.draft };
    const deletedId = deletedActivity.id;
    this.subscriptions.add(this.activitiesRealApi.deleteActivity(deletedId).subscribe({
      next: () => {
        this.close();
        if (!this.embeddedInGroups) {
          this.closeDeleteDialog();
        }
        this.activityDeleted.emit(deletedActivity);
        this.cdr.markForCheck();
      },
      error: () => {
        this.setOperationError('Unable to delete activity. Please try again.');
        this.closeDeleteDialog();
        this.cdr.markForCheck();
      }
    }));
  }

  openCancelDialog() {
    this.cancelDialogClosing = false;
    this.skipCancelDialogEnterAnimation = false;
    this.cancelConfirmed = false;
    this.cancelDialogVisible = true;
  }

  closeCancelDialog() {
    this.skipCancelDialogEnterAnimation = false;
    this.cancelDialogVisible = false;
    this.cancelDialogClosing = true;
  }

  onCancelDialogHidden() {
    if (this.cancelDialogVisible) {
      return;
    }

    this.cancelDialogClosing = false;
    this.skipCancelDialogEnterAnimation = false;
    this.cancelConfirmed = false;
  }

  onCancelConfirmedChange(value: boolean) {
    this.cancelConfirmed = value;
  }

  onCancelConfirmed() {
    if (!this.state.draft?.id) {
      return;
    }

    const activityId = this.state.draft.id;
    const activityName = this.state.draft.name;
    this.closeCancelDialog();

    this.subscriptions.add(this.activitiesRealApi.cancelActivity(activityId).subscribe({
      next: () => {
        const cancelledActivity: EditGroupActivity = {
          id: this.state.draft!.id!,
          name: this.state.draft!.name ?? '',
          time: this.state.draft!.time ?? '',
          date: this.state.draft!.date ?? '',
          thumbnailUrl: this.state.draft!.thumbnailUrl ?? '',
          sport: this.state.draft!.sport ?? '',
          location: this.state.draft!.location ?? '',
          host: this.state.draft!.host ?? '',
          participants: this.state.draft!.participants ?? 0,
          ...this.state.draft
        };
        this.close();
        this.activityCancelled.emit(cancelledActivity);
        this.cdr.markForCheck();
      },
      error: () => {
        this.setOperationError('Unable to cancel activity. Please try again.');
        this.cdr.markForCheck();
      }
    }));
  }

  openCancelPaidDialog() {
    this.cancelPaidDialogClosing = false;
    this.skipCancelPaidDialogEnterAnimation = false;
    this.cancelPaidConfirmed = false;
    this.cancelPaidDialogVisible = true;
  }

  closeCancelPaidDialog() {
    this.skipCancelPaidDialogEnterAnimation = false;
    this.cancelPaidDialogVisible = false;
    this.cancelPaidDialogClosing = true;
  }

  onCancelPaidDialogHidden() {
    if (this.cancelPaidDialogVisible) {
      return;
    }

    this.cancelPaidDialogClosing = false;
    this.skipCancelPaidDialogEnterAnimation = false;
    this.cancelPaidConfirmed = false;
  }

  onCancelPaidConfirmedChange(value: boolean) {
    this.cancelPaidConfirmed = value;
  }

  onCancelPaidConfirmed() {
    if (!this.state.draft?.id) {
      return;
    }

    const activityId = this.state.draft.id;
    const activityName = this.state.draft.name;
    this.closeCancelPaidDialog();

    this.subscriptions.add(this.activitiesRealApi.cancelActivity(activityId).subscribe({
      next: () => {
        const cancelledActivity: EditGroupActivity = {
          id: this.state.draft!.id!,
          name: this.state.draft!.name ?? '',
          time: this.state.draft!.time ?? '',
          date: this.state.draft!.date ?? '',
          thumbnailUrl: this.state.draft!.thumbnailUrl ?? '',
          sport: this.state.draft!.sport ?? '',
          location: this.state.draft!.location ?? '',
          host: this.state.draft!.host ?? '',
          participants: this.state.draft!.participants ?? 0,
          ...this.state.draft
        };
        this.close();
        this.activityCancelled.emit(cancelledActivity);
        this.cdr.markForCheck();
      },
      error: () => {
        this.setOperationError('Unable to cancel activity. Please try again.');
        this.cdr.markForCheck();
      }
    }));
  }

  handleDeleteOrCancel() {
    if (!this.state.draft) {
      return;
    }

    const isIncoming = this.state.draft.status === 'incoming';
    const isFree = this.state.draft.type === 'free' || !this.state.draft.type;
    const hasNoParticipants = this.state.draft.participants === 0;
    const isPaid = this.state.draft.type === 'paid';
    const hasParticipants = this.state.draft.participants > 0;

    if (isIncoming && isPaid && hasParticipants) {
      this.openCancelPaidDialog();
      return;
    }

    if (isIncoming && (isFree || hasNoParticipants)) {
      this.openCancelDialog();
      return;
    }

    this.openDeleteDialog();
  }

  createDeleteRequest(): ActivityDrawerDeleteRequest | null {
    if (!this.state.draft) {
      return null;
    }

    return {
      activity: this.state.draft,
      deleteTarget: this.toDeleteTarget(this.state.draft)
    };
  }

  getActivityCoverPhotoSrc(): string {
    if (this.state.coverPhotoFailed || !this.isRemovablePhotoUrl(this.state.draft?.thumbnailUrl)) {
      return this.defaultCoverPhotoUrl;
    }

    return this.state.draft?.thumbnailUrl || this.defaultCoverPhotoUrl;
  }

  hasActivityCoverPhoto(): boolean {
    return !this.state.coverPhotoFailed && this.isRemovablePhotoUrl(this.state.draft?.thumbnailUrl);
  }

  onCoverPhotoError() {
    this.state.coverPhotoFailed = true;
    this.emitSessionChange();
    this.cdr.markForCheck();
  }

  toggleCoverPhotoMenu(event: Event) {
    event.stopPropagation();
    this.state.coverPhotoMenuVisible = !this.state.coverPhotoMenuVisible;
    this.emitSessionChange();
    this.cdr.markForCheck();
  }

  closeCoverPhotoMenu() {
    if (!this.state.coverPhotoMenuVisible) {
      return;
    }

    this.state.coverPhotoMenuVisible = false;
    this.emitSessionChange();
    this.cdr.markForCheck();
  }

  async onCoverPhotoSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const preview = await this.photoService.createPreview(file);
    if (!this.state.draft?.id) {
      return;
    }

    if (preview.error || !preview.dataUrl || !preview.file) {
      this.setOperationError(preview.error ?? 'Unable to process this image.');
      this.state.coverPhotoMenuVisible = false;
      this.cdr.markForCheck();
      return;
    }

    this.state.draft = {
      ...this.state.draft,
      thumbnailUrl: preview.dataUrl,
      backgroundImageFile: preview.file,
      backgroundImageRemoved: false
    };
    this.state.coverPhotoFailed = false;
    this.state.coverPhotoMenuVisible = false;
    this.setOperationError('');
    this.emitSessionChange();
    this.cdr.markForCheck();
  }

  removeCoverPhoto() {
    if (!this.state.draft?.id || !this.hasActivityCoverPhoto()) {
      return;
    }

    const originalPhotoUrl = this.state.original?.thumbnailUrl ?? '';
    const originalHasPhoto = this.isRemovablePhotoUrl(originalPhotoUrl);
    this.state.draft = {
      ...this.state.draft,
      thumbnailUrl: originalHasPhoto ? '' : originalPhotoUrl,
      backgroundImageFile: undefined,
      backgroundImageRemoved: originalHasPhoto
    };
    this.state.coverPhotoFailed = false;
    this.state.coverPhotoMenuVisible = false;
    this.setOperationError('');
    this.emitSessionChange();
    this.cdr.markForCheck();
  }

  editName() {
    this.state.editingName = true;
    this.emitSessionChange();
    this.cdr.markForCheck();
  }

  editDescription() {
    this.state.editingDescription = true;
    this.emitSessionChange();
    this.cdr.markForCheck();
  }

  editRouteUrl() {
    this.state.editingRouteUrl = true;
    this.emitSessionChange();
    this.cdr.markForCheck();
  }

  editSport() {
    this.state.editingSport = true;
    this.emitSessionChange();
    this.cdr.markForCheck();
  }

  editLevel() {
    this.state.editingLevel = true;
    this.emitSessionChange();
    this.cdr.markForCheck();
  }

  undoName() {
    if (this.state.draft && this.state.original) {
      this.state.draft = { ...this.state.draft, name: this.state.original.name };
      this.state.editingName = this.keepUndoInEditMode;
    }
    this.emitSessionChange();
    this.cdr.markForCheck();
  }

  undoDescription() {
    if (this.state.draft && this.state.original) {
      this.state.draft = { ...this.state.draft, description: this.state.original.description };
      this.state.editingDescription = this.keepUndoInEditMode;
    }
    this.emitSessionChange();
    this.cdr.markForCheck();
  }

  undoRouteUrl() {
    if (this.state.draft && this.state.original) {
      this.state.draft = { ...this.state.draft, routeUrl: this.state.original.routeUrl };
      this.state.editingRouteUrl = this.keepUndoInEditMode;
    }
    this.emitSessionChange();
    this.cdr.markForCheck();
  }

  onSportChange(value: string) {
    if (this.state.draft) {
      const sport = this.getSportLabelFromValue(value);
      this.state.draft = { ...this.state.draft, sport, sportId: value };
      if (!this.activityPreview.isLevelValueValidForSport(
        this.state.level,
        sport,
        this.sportTypeConfigs
      )) {
        this.state.level = 'all';
      }
    }
    this.emitSessionChange();
    this.cdr.markForCheck();
  }

  undoSport() {
    if (this.state.draft && this.state.original) {
      this.state.draft = {
        ...this.state.draft,
        sport: this.state.original.sport,
        sportId: this.state.original.sportId
      };
      if (!this.activityPreview.isLevelValueValidForSport(
        this.state.level,
        this.state.original.sport,
        this.sportTypeConfigs
      )) {
        this.state.level = this.state.originalLevel;
      }
      this.state.editingSport = this.keepUndoInEditMode;
    }
    this.emitSessionChange();
    this.cdr.markForCheck();
  }

  onLevelChange(value: string) {
    this.state.level = value;
    this.emitSessionChange();
    this.cdr.markForCheck();
  }

  undoLevel() {
    this.state.level = this.state.originalLevel;
    this.state.editingLevel = this.keepUndoInEditMode;
    this.emitSessionChange();
    this.cdr.markForCheck();
  }

  toggleParticipantsExpanded() {
    if (this.state.participantsExpanded) {
      this.state.participantsExpanded = false;
      this.emitSessionChange();
      this.cdr.markForCheck();
      return;
    }

    const activity = this.state.draft;
    if (!activity?.id || this.participantsLoading) {
      return;
    }

    const loadedCount = activity.participantDetails?.length ?? 0;
    if (loadedCount >= activity.participants) {
      this.state.participantsExpanded = true;
      this.emitSessionChange();
      this.cdr.markForCheck();
      return;
    }

    this.participantsLoading = true;
    this.subscriptions.add(this.activitiesRealApi.getParticipants(
      activity.id,
      1,
      Math.max(activity.participants, 10)
    ).subscribe({
      next: section => {
        this.participantsLoading = false;
        if (this.state.draft?.id !== activity.id) {
          return;
        }

        this.state.draft = {
          ...this.state.draft,
          participantDetails: section.data,
          participants: section.total
        };
        this.state.original = this.state.original
          ? {
              ...this.state.original,
              participantDetails: section.data,
              participants: section.total
            }
          : null;
        this.state.participantsExpanded = true;
        this.emitSessionChange();
        this.cdr.markForCheck();
      },
      error: error => {
        this.participantsLoading = false;
        this.setOperationError(this.getParticipantsErrorMessage(error));
        this.cdr.markForCheck();
      }
    }));
  }

  private getParticipantsErrorMessage(error: unknown): string {
    const detailError = this.getDetailErrorMessage(error);
    return detailError.replace('activity detail', 'activity participants');
  }

  dismissSaveSuccess() {
    this.state.saveSuccessVisible = false;
    this.clearToastTimeout();
    this.emitSessionChange();
    this.cdr.markForCheck();
  }

  dismissDiscardSuccess() {
    this.state.discardSuccessVisible = false;
    this.clearToastTimeout();
    this.emitSessionChange();
    this.cdr.markForCheck();
  }

  dismissOperationError() {
    this.setOperationError('');
  }

  discardChanges() {
    if (this.state.original) {
      this.state.draft = { ...this.state.original };
    }

    this.state.level = this.state.originalLevel;
    this.state.saveSuccessVisible = false;
    this.state.discardSuccessVisible = true;
    this.state.editingName = false;
    this.state.editingDescription = false;
    this.state.editingRouteUrl = false;
    this.state.editingSport = false;
    this.state.editingLevel = false;
    this.state.coverPhotoMenuVisible = false;
    this.keepUndoInEditMode = false;
    this.state.keepUndoInEditMode = false;
    this.scheduleToastDismiss(() => {
      this.state.discardSuccessVisible = false;
      this.cdr.markForCheck();
    });
    this.emitSessionChange();
    this.cdr.markForCheck();
  }

  saveChanges() {
    if (
      !this.state.draft?.id ||
      !this.activityPreview.canSave(
        this.state.draft,
        this.state.original,
        this.state.level,
        this.state.originalLevel,
        this.defaultCoverPhotoUrl
      )
    ) {
      return;
    }

    const nextActivity: EditGroupActivity = {
      ...this.state.draft,
      level: this.state.level === 'all' ? 'All levels' : `Level ${this.state.level}`
    };

    const sportId = this.sportTypeConfigs.length
      ? this.getSportOptionValue(nextActivity.sport)
      : nextActivity.sportId ?? this.getSportOptionValue(nextActivity.sport);
    this.subscriptions.add(this.activitiesRealApi
      .updateActivity(nextActivity.id, nextActivity, sportId, this.state.level)
      .pipe(switchMap(() => this.embeddedInGroups
        ? of(nextActivity)
        : this.activitiesRealApi.getDetail(nextActivity.id)))
      .subscribe({
      next: serverActivity => {
        const savedActivity = {
          ...nextActivity,
          ...serverActivity,
          status: serverActivity.status ?? nextActivity.status,
          backgroundImageFile: undefined,
          backgroundImageRemoved: false
        };
        this.state.draft = savedActivity;
        this.state.original = { ...savedActivity };
        this.state.originalLevel = this.state.level;
        this.state.discardSuccessVisible = false;
        this.state.editingName = false;
        this.state.editingDescription = false;
        this.state.editingRouteUrl = false;
        this.state.editingSport = false;
        this.state.editingLevel = false;
        this.state.coverPhotoMenuVisible = false;
        this.keepUndoInEditMode = false;
        this.state.keepUndoInEditMode = false;
        this.state.saveSuccessVisible = true;
        this.activitySaved.emit(savedActivity);
        this.scheduleToastDismiss(() => {
          this.state.saveSuccessVisible = false;
          this.cdr.markForCheck();
        });
        this.emitSessionChange();
        this.cdr.markForCheck();
      },
      error: error => {
        this.setOperationError(this.getSaveErrorMessage(error));
        this.cdr.markForCheck();
      }
    }));
  }

  openDeleteDialog() {
    if (!this.state.draft) {
      return;
    }

    if (this.embeddedInGroups) {
      const request = this.createDeleteRequest();
      if (request) {
        this.deleteRequested.emit(request);
      }
      return;
    }

    this.deleteConfirmed = false;
    this.deleteDialogClosing = false;
    this.deleteDialogVisible = true;
    this.emitSessionChange();
    this.cdr.detectChanges();
  }

  closeDeleteDialog() {
    if (!this.deleteDialogVisible && !this.deleteDialogClosing) {
      this.deleteConfirmed = false;
      return;
    }

    this.deleteDialogClosing = true;
    this.deleteDialogVisible = false;
    this.emitSessionChange();
    this.cdr.detectChanges();
  }

  onDeleteDialogHidden() {
    this.deleteDialogClosing = false;
    this.deleteConfirmed = false;
    this.emitSessionChange();
    this.cdr.markForCheck();
  }

  confirmStandaloneDelete() {
    if (!this.deleteConfirmed) {
      return;
    }

    this.confirmDeleteFromParent();
  }

  onDraftChanged() {
    this.emitSessionChange();
    this.cdr.markForCheck();
  }

  markInitialized() {
    this.initialized = true;
  }

  private resolveActivityDetail(activity: EditGroupActivity): EditGroupActivity {
    return { ...activity };
  }

  private getSaveErrorMessage(error: unknown): string {
    const uploadErrorMessage = this.getActivityUploadErrorMessage(error);
    if (uploadErrorMessage) {
      return uploadErrorMessage;
    }

    return this.getDetailErrorMessage(error)
      .replace('Unable to load activity detail', 'Unable to save activity changes');
  }

  private hydrateActivityDetail(activityId: string) {
    if (this.hydratingActivityId === activityId) {
      return;
    }

    this.hydratingActivityId = activityId;
    this.subscriptions.add(this.activitiesRealApi.getDetail(activityId).subscribe({
      next: activity => {
        this.hydratingActivityId = '';
        if (!activity || this.state.draft?.id !== activityId) {
          return;
        }

        const hydratedActivity = {
          ...this.state.draft,
          ...activity,
          status: activity.status ?? this.state.draft.status
        };
        this.state.draft = hydratedActivity;
        this.state.original = { ...hydratedActivity };
        this.state.level = this.activityPreview.getLevelValue(
          hydratedActivity.level,
          hydratedActivity.sport,
          this.sportTypeConfigs
        );
        this.state.originalLevel = this.state.level;
        this.emitSessionChange();
        this.cdr.markForCheck();
      },
      error: error => {
        this.hydratingActivityId = '';
        this.setOperationError(this.getDetailErrorMessage(error));
        this.cdr.markForCheck();
      }
    }));
  }

  private getDetailErrorMessage(error: unknown): string {
    if (!error || typeof error !== 'object') {
      return typeof error === 'string' && error.trim()
        ? error
        : 'Unable to load activity detail. Please try again.';
    }

    const apiError = error as {
      status?: unknown;
      message?: unknown;
      error?: {
        message?: unknown;
        statusCode?: unknown;
      } | string;
    };
    const status = typeof apiError.status === 'number'
      ? apiError.status
      : typeof apiError.error === 'object' && typeof apiError.error?.statusCode === 'number'
        ? apiError.error.statusCode
        : undefined;
    const message = typeof apiError.error === 'object' && typeof apiError.error?.message === 'string'
      ? apiError.error.message
      : typeof apiError.error === 'string'
        ? apiError.error
        : typeof apiError.message === 'string'
          ? apiError.message
          : '';

    if (status || message) {
      return `Unable to load activity detail${message ? `: ${message}` : '. Please try again.'}`;
    }

    return 'Unable to load activity detail. Please try again.';
  }

  private getActivityUploadErrorMessage(error: unknown): string | null {
    const status = this.getApiStatus(error);
    switch (status) {
      case 0:
        return 'Cannot connect to the server right now. Please check your connection and try again.';
      case 413:
        return 'The selected photo is too large. Please choose a smaller image and try again.';
      case 415:
        return 'This file type is not supported. Please choose a different image.';
      default:
        return null;
    }
  }

  private getApiStatus(error: unknown): number | undefined {
    if (!error || typeof error !== 'object') {
      return undefined;
    }

    const apiError = error as {
      status?: unknown;
      statusCode?: unknown;
      error?: {
        status?: unknown;
        statusCode?: unknown;
      } | string;
    };

    if (typeof apiError.status === 'number') {
      return apiError.status;
    }

    if (typeof apiError.statusCode === 'number') {
      return apiError.statusCode;
    }

    if (typeof apiError.error === 'object') {
      if (typeof apiError.error.status === 'number') {
        return apiError.error.status;
      }

      if (typeof apiError.error.statusCode === 'number') {
        return apiError.error.statusCode;
      }
    }

    return undefined;
  }

  private ensureActivitiesStoreReady() {
    // Standalone page initializes the store itself before opening drawers.
  }

  private emitSessionChange() {
    this.sessionChange.emit();
  }

  private emitVisibilityChange() {
    this.visibilityChange.emit(this.state.visible);
  }

  private setOperationError(message: string) {
    this.externalOperationErrorChange.emit(message);
  }

  getSportOptionValue(sport: string): string {
    return getSportOptionValue(sport, this.sportTypeConfigs);
  }

  private getSportLabelFromValue(value: string): string {
    return getSportLabelFromValue(value, this.sportTypeConfigs);
  }

  private isRemovablePhotoUrl(photoUrl: string | null | undefined): boolean {
    return (
      !!photoUrl &&
      photoUrl !== this.defaultCoverPhotoUrl &&
      !photoUrl.includes('group-cover-default') &&
      !photoUrl.includes('placeholder')
    );
  }

  private toDeleteTarget(activity: EditGroupActivity): ManagementDeleteTarget {
    return {
      id: activity.id,
      name: activity.name
    };
  }

  private scheduleToastDismiss(dismiss: () => void) {
    this.toastDismiss.schedule(dismiss, () => {
      this.emitSessionChange();
      this.cdr.markForCheck();
    });
  }

  private clearToastTimeout() {
    this.toastDismiss.clear();
  }
}
